import { useEffect, useState } from 'react';
import MatchRow from './MatchRow';
import { RoundStatusBadge } from './TournamentBadges';
import { MemberChip } from '../memberDisplay';
import { btbStageOf, btbGroupLabel, btbBoxLabel } from './tournamentConstants';

// Цвета групп Beat the Box (этап 2)
const BTB_GROUP_COLORS = {
  Gold:    { bg: '#fef9c3', border: '#fde047', color: '#854d0e' },
  Silver:  { bg: '#f1f5f9', border: '#cbd5e1', color: '#475569' },
  Bronze:  { bg: '#fff7ed', border: '#fed7aa', color: '#92400e' },
  Crystal: { bg: '#f0f9ff', border: '#bae6fd', color: '#075985' },
};

const RoundCard = ({ round, playerMap, canEdit, publicId, tournament, onScoreSaved, tournamentFinished, onScoreModalOpen, onScoreModalClose, clientId }) => {
  // В завершённом турнире раунды раскрыты — пользователь видит итоги без лишних кликов.
  // В RUNNING завершённые раунды свёрнуты, чтобы не засорять экран.
  const [open, setOpen] = useState(tournamentFinished || round.status !== 'FINISHED');

  // Beat the Box: этап и лейблы боксов/групп
  const isBtb = tournament?.format === 'BEAT_THE_BOX';
  const btbStage = isBtb ? btbStageOf(round.roundNumber) : null;
  const sittingPlayers = (round.sittingOutPlayerIds || [])
    .map(id => playerMap[id])
    .filter(Boolean);

  // Авто-сворачивание: когда все матчи получили счёт и раунд стал FINISHED —
  // сворачиваем с небольшой задержкой, чтобы пользователь увидел последний счёт.
  useEffect(() => {
    if (round.status === 'FINISHED' && !tournamentFinished) {
      const t = setTimeout(() => setOpen(false), 800);
      return () => clearTimeout(t);
    }
  }, [round.status, tournamentFinished]);

  return (
    <div
      id={`round-${round.id}`}
      style={{
        background: '#fff', borderRadius: 14,
        border: '1.5px solid #f0f0f5',
        boxShadow: '0 1px 6px rgba(0,0,0,0.05)',
        overflow: 'hidden',
      }}
    >
      {/* Заголовок раунда — кликабельный */}
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', background: 'none', border: 'none',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '10px 16px', cursor: 'pointer',
          borderBottom: open ? '1px solid #f0f0f5' : 'none',
          transition: 'border-color 0.3s',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>
            Раунд {round.roundNumber}
          </span>
          <RoundStatusBadge status={round.status} />
          {isBtb && (
            <span style={{
              fontSize: 10, fontWeight: 700, letterSpacing: 0.3,
              padding: '2px 7px', borderRadius: 99,
              background: btbStage === 1 ? '#f0f9ff' : '#f0fdf4',
              color: btbStage === 1 ? '#0369a1' : '#15803d',
              border: `1px solid ${btbStage === 1 ? '#bae6fd' : '#86efac'}`,
            }}>
              Этап {btbStage}
            </span>
          )}
        </div>
        <span style={{ fontSize: 18, color: '#9ca3af', lineHeight: 1 }}>
          {open ? '−' : '+'}
        </span>
      </button>

      {/* Анимированное тело раунда — CSS Grid trick: 0fr ↔ 1fr */}
      <div style={{
        display: 'grid',
        gridTemplateRows: open ? '1fr' : '0fr',
        transition: 'grid-template-rows 0.4s ease',
      }}>
        <div style={{ overflow: 'hidden' }}>
        <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {(round.matches || []).map(match => {
            // Beat the Box: показываем заголовок бокса/группы над каждым матчем
            let btbMatchLabel = null;
            if (isBtb) {
              const court = match.courtNumber;
              if (btbStage === 1) {
                btbMatchLabel = { text: btbBoxLabel(court), bg: '#f8fafc', border: '#e5e7eb', color: '#374151' };
              } else {
                const gLabel = btbGroupLabel(court);
                const gColor = BTB_GROUP_COLORS[gLabel] || BTB_GROUP_COLORS.Gold;
                btbMatchLabel = { text: gLabel, ...gColor };
              }
            }
            return (
              <div key={match.id}>
                {btbMatchLabel && (
                  <div style={{
                    fontSize: 11, fontWeight: 700, letterSpacing: 0.4,
                    padding: '3px 8px', borderRadius: 6, marginBottom: 4,
                    display: 'inline-block',
                    background: btbMatchLabel.bg,
                    border: `1px solid ${btbMatchLabel.border}`,
                    color: btbMatchLabel.color,
                  }}>
                    {btbMatchLabel.text}
                  </div>
                )}
                <MatchRow
                  match={match}
                  playerMap={playerMap}
                  canEdit={canEdit}
                  publicId={publicId}
                  tournament={tournament}
                  roundNumber={round.roundNumber}
                  onScoreSaved={onScoreSaved}
                  onScoreModalOpen={onScoreModalOpen}
                  onScoreModalClose={onScoreModalClose}
                  clientId={clientId}
                />
              </div>
            );
          })}

          {sittingPlayers.length > 0 && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
              padding: '8px 10px', borderRadius: 8,
              background: '#fafafa', border: '1px solid #f0f0f5',
              fontSize: 12, color: '#6b7280',
            }}>
              <span style={{ fontWeight: 600, flexShrink: 0 }}>Отдыхают:</span>
              {sittingPlayers.map((p, i) => (
                <MemberChip key={i} member={p} fontSize={12} />
              ))}
            </div>
          )}
        </div>
        </div>
      </div>
    </div>
  );
};

export default RoundCard;
